import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input } from '../components/ui/index.js';

const API_BASE = '/v1';

interface FormData {
  email: string;
  password: string;
  businessName: string;
  phone: string;
}

type PasswordStrength = 'weak' | 'medium' | 'strong';

function getPasswordStrength(password: string): PasswordStrength {
  if (password.length < 12) return 'weak';
  let variety = 0;
  if (/[a-z]/.test(password)) variety++;
  if (/[A-Z]/.test(password)) variety++;
  if (/[0-9]/.test(password)) variety++;
  if (/[^a-zA-Z0-9]/.test(password)) variety++;
  if (variety >= 3 && password.length >= 16) return 'strong';
  if (variety >= 2) return 'medium';
  return 'weak';
}

const strengthConfig: Record<PasswordStrength, { label: string; color: string; width: string }> = {
  weak: { label: 'Weak', color: 'bg-red-500', width: 'w-1/3' },
  medium: { label: 'Medium', color: 'bg-yellow-500', width: 'w-2/3' },
  strong: { label: 'Strong', color: 'bg-green-500', width: 'w-full' },
};

export function SignupPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    businessName: '',
    phone: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep1 = (): boolean => {
    if (!formData.email) {
      setError('Email is required.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    if (formData.password.length < 12) {
      setError('Password must be at least 12 characters.');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!formData.businessName || formData.businessName.trim().length < 2) {
      setError('Business name must be at least 2 characters.');
      return false;
    }
    if (!formData.phone) {
      setError('Phone number is required.');
      return false;
    }
    return true;
  };

  const handleNext = (e: FormEvent) => {
    e.preventDefault();
    if (validateStep1()) {
      setError('');
      setStep(2);
    }
  };

  const handleBack = () => {
    setError('');
    setStep(1);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setIsLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          business_name: formData.businessName.trim(),
          phone: formData.phone,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(data.message || 'Something went wrong. Please try again.');
      }

      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      const res = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      if (res.ok) {
        setResendSuccess(true);
      }
    } catch {
      // Silently fail on resend
    } finally {
      setResendLoading(false);
    }
  };

  const strength = getPasswordStrength(formData.password);
  const cfg = strengthConfig[strength];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">97</span>
          </div>
          <h1 className="text-xl font-semibold text-white">Start Your Free Trial</h1>
          <p className="text-primary-200 text-sm mt-1">Get lead notifications in minutes</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          {/* Step indicator */}
          {step < 3 && (
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2].map(s => (
                <div
                  key={s}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    s <= step ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-100 flex items-center gap-2 mb-5">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          {/* Step 1: Your Account */}
          {step === 1 && (
            <form onSubmit={handleNext} className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Your Account</h2>
                <p className="text-sm text-gray-500 mb-4">Create your login credentials</p>
              </div>

              <Input
                label="Email address"
                id="email"
                type="email"
                value={formData.email}
                onChange={e => updateField('email', e.target.value)}
                required
                autoFocus
                autoComplete="email"
                placeholder="you@business.com"
              />

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={e => updateField('password', e.target.value)}
                    required
                    minLength={12}
                    autoComplete="new-password"
                    className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50"
                    placeholder="Min 12 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {showPassword ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      )}
                    </svg>
                  </button>
                </div>

                {/* Password strength indicator */}
                {formData.password.length > 0 && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${cfg.color} ${cfg.width}`}
                      />
                    </div>
                    <p className={`text-xs mt-1 ${
                      strength === 'weak' ? 'text-red-600' :
                      strength === 'medium' ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {cfg.label}
                      {strength === 'weak' && formData.password.length < 12 && ' — at least 12 characters required'}
                    </p>
                  </div>
                )}
              </div>

              <Button variant="primary" size="lg" className="w-full" type="submit">
                Next
              </Button>
            </form>
          )}

          {/* Step 2: Your Business */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Your Business</h2>
                <p className="text-sm text-gray-500 mb-4">Tell us about your business</p>
              </div>

              <Input
                label="Business name"
                id="businessName"
                type="text"
                value={formData.businessName}
                onChange={e => updateField('businessName', e.target.value)}
                required
                autoFocus
                placeholder="Acme Fitness"
              />

              <Input
                label="Phone number"
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={e => updateField('phone', e.target.value)}
                required
                placeholder="(555) 123-4567"
              />

              {/* Info box */}
              <div className="bg-primary-50 text-primary-800 text-sm p-3 rounded-lg border border-primary-100 flex items-start gap-2">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                You'll start getting lead notifications right away!
              </div>

              <div className="flex gap-3">
                <Button variant="secondary" size="lg" className="flex-1" type="button" onClick={handleBack}>
                  Back
                </Button>
                <Button variant="primary" size="lg" loading={isLoading} className="flex-1" type="submit">
                  {isLoading ? 'Creating...' : 'Start Free Trial'}
                </Button>
              </div>
            </form>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="text-center py-4 space-y-5">
              {/* Success checkmark */}
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-[scale-in_0.3s_ease-out]">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Check your email!</h2>
                <p className="text-sm text-gray-500">
                  We sent a verification link to <span className="font-medium text-gray-700">{formData.email}</span>. Click it to access your dashboard.
                </p>
              </div>

              <div className="pt-2">
                {resendSuccess ? (
                  <p className="text-sm text-green-600 font-medium">Verification email resent!</p>
                ) : (
                  <p className="text-sm text-gray-500">
                    Didn't get it?{' '}
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resendLoading}
                      className="text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                    >
                      {resendLoading ? 'Sending...' : 'Resend verification email'}
                    </button>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bottom link */}
        <p className="text-center text-xs text-primary-200 mt-6">
          {step < 3 ? (
            <>
              Already have an account?{' '}
              <Link to="/login" className="text-white hover:underline font-medium">
                Sign in
              </Link>
            </>
          ) : (
            'Powered by 97 Display'
          )}
        </p>
      </div>
    </div>
  );
}
